import { ActionTakenType, ProfileCollectPayload, ProfileCollectStatus, createActionTaken } from "@swivl-mono/types";
import parsePhoneNumberFromString from "libphonenumber-js";
import React, { Component, useContext, useEffect, useState } from "react";

import { Spinner } from "../../../../components/Spinner/Spinner";
import { LanguageContext, LocaleStrings, t } from "../../../../i18n/strings";
import { MessageHandlerInterface } from "../../../../messages/MessageHandler";
import { ListItemData } from "../../ListItem";
import "./ProfileCollect.scss";
import { PROFILE_VALIDATION_ERROR, ProfileCollectValidationError, ProfileValidationErrorCode, ValidateProfileCollect } from "./ProfileValidator";

const VALIDATION_ERROR_TO_I18N: Record<ProfileValidationErrorCode, keyof LocaleStrings> = {
  [PROFILE_VALIDATION_ERROR.REQUIRED]: "profileCollectErrorRequired",
  [PROFILE_VALIDATION_ERROR.INVALID_EMAIL]: "profileCollectErrorInvalidEmail",
  [PROFILE_VALIDATION_ERROR.INVALID_PHONE]: "profileCollectErrorInvalidPhone",
  [PROFILE_VALIDATION_ERROR.INVALID_POSTAL_CODE]: "profileCollectErrorInvalidPostalCode",
  [PROFILE_VALIDATION_ERROR.NAME_TOO_SHORT]: "profileCollectErrorNameTooShort"
};

export interface ProfileCollectProps {
  item: ListItemData;
  messageHandler: MessageHandlerInterface;
  isActive: boolean;
}
export interface ProfileCollectState {
  selectedActions: Array<string>;
}
export function ProfileCollect(props: ProfileCollectProps) {
  const language = useContext(LanguageContext);
  const { item, isActive, messageHandler } = props;
  const [state, setState] = useState<ProfileCollectPayload>(item.message.payload as ProfileCollectPayload);
  const [saving, setSaving] = useState<boolean>(false);
  const [validationErrors, setValidationErrors] = useState<ProfileCollectValidationError>({});
  const payload = item.message.payload as ProfileCollectPayload;

  useEffect(() => {
    setState(payload);
  }, [item.message.payload]);

  const submitClicked = () => {
    setSaving(true);
    const originalPayload = item.message.translation?.originalPayload as ProfileCollectPayload | undefined;
    const validations = ValidateProfileCollect(payload, originalPayload);
    if (Object.values(validations.validationErrors).length) {
      setValidationErrors(validations.validationErrors);
      setSaving(false);
      return;
    }
    setState(validations.payload);

    const messageId = item.message.uuid;
    const actionTaken = createActionTaken(ActionTakenType.Other, "saved", { items: validations.payload.items });

    messageHandler.actionTaken(messageId, actionTaken);

    setSaving(false);

    const el = document.getElementById("scc-form-input");
    if (el) {
      el.focus();
    }
  };
  const cancelClicked = () => {
    const messageId = item.message.uuid;
    const blankState = { ...state };

    state.items.map((item, index) => {
      blankState.items[index] = { ...item, value: "" };
    });
    setState(blankState);
    const actionTaken = createActionTaken(ActionTakenType.Other, "canceled", {});
    messageHandler.actionTaken(messageId, actionTaken);
  };

  const actionTaken = item.message.actionTaken;

  const active = !actionTaken && isActive && payload.status === ProfileCollectStatus.PENDING;
  const actionTakenItems = item.message.actionTaken?.data?.items;

  return (
    <div className="scc-ProfileCollect">
      <div className="scc-ProfileCollectCard">
        {state.items.map((item, index) => {
          let value = actionTakenItems?.[index]?.value || item.value;
          if (!active && (!value || !value.length)) {
            value = "--";
          }

          return (
            <div
              className={validationErrors[item.id]?.length ? "scc-ProfileCollectCardItem scc-validationError" : "scc-ProfileCollectCardItem"}
              key={index}
            >
              <label>
                {item.label} {!item.required && active ? ` ${t("profileCollectOptional", language)} ` : null}{" "}
                {active && (
                  <span className="scc-profileValidationError">
                    {validationErrors[item.label] ? t(VALIDATION_ERROR_TO_I18N[validationErrors[item.label]], language) : null}
                  </span>
                )}
              </label>
              {active ? (
                <input
                  type="text"
                  value={value}
                  placeholder={item.placeholder}
                  onKeyUp={(e) => {
                    if (e.key === "Enter") {
                      submitClicked();
                    }
                  }}
                  onChange={(e) => {
                    const newState = { ...state };
                    newState.items[index].value = e.target.value;
                    setState(newState);
                  }}
                />
              ) : (
                <div className="scc-ProfileCollectCardItemValue">{value}</div>
              )}
            </div>
          );
        })}
        {active && (
          <div className="scc-ProfileCollectCardButtons">
            <button
              className="scc-ProfileCancelButton"
              onClick={() => {
                cancelClicked();
              }}
            >
              {t("profileCollectCancel", language)}
            </button>
            <button
              className="scc-ProfileSaveButton swivl-primaryButton"
              disabled={saving}
              onClick={() => {
                if (saving) {
                  return;
                }
                submitClicked();
              }}
            >
              {saving ? <Spinner /> : state.actionButtonText}
            </button>
          </div>
        )}
      </div>
      {!active && actionTaken && actionTaken.target === "canceled" && (
        <div className="scc-ProfileInfoLine">{t("profileCollectCanceled", language)}</div>
      )}
      {active && <div className="scc-ProfileInfoLine">{t("profileCollectInstruction", language)}</div>}
    </div>
  );
}
